import React from "react";
import { getVariantLabel } from "@leafly-com/web-utils";
import Link from "next/link";

import { CartItem } from "custom-types/Cart";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { updateCart } from "redux/action-creators/updateCart";
import { useDispatch, useSelector } from "redux/hooks";
import {
  getCartContext,
  getCartIsInStore,
  getCartSessionId,
} from "redux/selectors/cart";
import { calculateItemTotals } from "utils/calculateCartTotals";
import getMenuItemImage from "utils/getMenuItemImage";
import { getMenuItemUrl } from "utils/menuItemUrl";

import Notification, { NotificationSize } from "../Notification";
import CartItemImage from "./CartItemImage";

type Props = {
  item: CartItem;
  editable: boolean;
  warning?: string;
  isEmbedded?: boolean;
};

const CartItemCard = ({
  item,
  editable,
  warning,
  isEmbedded = false,
}: Props) => {
  const dispatch = useDispatch();
  const domainCountryCode = useDomainCountryCode();

  const cartContext = useSelector(getCartContext);
  const isInStore = useSelector(getCartIsInStore);

  const variantLabel = getVariantLabel(item);
  const totals = calculateItemTotals(item);

  const sessionId = useSelector(getCartSessionId);
  const menuItemPath = getMenuItemUrl(
    {
      dispensarySlug: item.menuItemData.dispensarySlug,
      menuItemId: item.menuItemId,
      name: item.menuItemData.name,
      variants: item.menuItemData.variants,
    },
    item.variantId,
  );

  const menuItemImage = getMenuItemImage(item);

  return (
    <div className="flex flex-col bg-white border rounded border-light-grey p-4">
      {warning && (
        <Notification
          className="mb-md"
          message={warning}
          size={NotificationSize.SMALL}
          isFilled
        />
      )}

      <div className="flex items-center flex-auto">
        {isEmbedded ? (
          <CartItemImage
            menuItemImage={menuItemImage}
            altText={item.menuItemData?.name}
            category={item?.menuItemData?.category}
          />
        ) : (
          <Link href={menuItemPath} data-testid="menu-item-link">
            <CartItemImage
              menuItemImage={menuItemImage}
              altText={item.menuItemData?.name}
              category={item?.menuItemData?.category}
            />
          </Link>
        )}
        <div className="text-sm">
          <div className="font-bold">
            {isEmbedded ? (
              <div>
                {item.menuItemData.name && (
                  <span>{item.menuItemData.name}&nbsp;</span>
                )}
              </div>
            ) : (
              <Link href={menuItemPath}>
                <div>
                  {item.menuItemData.name && (
                    <span>{item.menuItemData.name}&nbsp;</span>
                  )}
                </div>
              </Link>
            )}
            <div className="text-xs">
              {variantLabel !== "each" && variantLabel}&nbsp;
            </div>
          </div>
          {editable && (
            <a
              data-testid="remove-item-button"
              onClick={(e) => {
                e.preventDefault();
                updateCart(
                  dispatch,
                  {
                    ...item,
                    quantity: 0,
                  },
                  isInStore ? "Removed Item - shop every menu" : "Removed Item",
                  sessionId,
                  true,
                  cartContext,
                  domainCountryCode,
                  isInStore,
                );
              }}
              className="text-xs font-bold underline cursor-pointer"
              href="/addItem"
            >
              Remove item
            </a>
          )}
        </div>
      </div>
      <div className="border-b my-md border-light-grey" />
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          {editable && (
            <button
              aria-label="Decrease quantity"
              data-testid="decrease-quantity-button"
              type="button"
              onClick={() => {
                updateCart(
                  dispatch,
                  {
                    ...item,
                    quantity: item.quantity - 1,
                  },
                  isInStore
                    ? "Decreased Quantity - shop every menu"
                    : "Decreased Quantity",
                  sessionId,
                  true,
                  cartContext,
                  domainCountryCode,
                  isInStore,
                );
              }}
              className="text-md mr-lg"
            >
              &ndash;
            </button>
          )}
          <input
            type="text"
            name="qty"
            step="1"
            min="1"
            value={item.quantity}
            readOnly={true}
            className="font-bold text-center"
            style={{ height: 50, width: 50 }}
            aria-label="Quantity"
          />
          {editable && (
            <button
              aria-label="Increase quantity"
              data-testid="increase-quantity-button"
              type="button"
              onClick={() => {
                updateCart(
                  dispatch,
                  {
                    ...item,
                    quantity: item.quantity + 1,
                  },
                  isInStore
                    ? "Increased Quantity - shop every menu"
                    : "Increased Quantity",
                  sessionId,
                  true,
                  cartContext,
                  domainCountryCode,
                  isInStore,
                );
              }}
              className="text-md ml-lg quantity-change-btn"
            >
              +
            </button>
          )}
        </div>
        <div className="text-right">
          <div
            className={`price ${totals.dealExists ? "text-notification" : ""}`}
          >
            <div className="font-bold text-md">
              ${totals.dealExists ? totals.discountedPrice : totals.price}
            </div>
          </div>
          {totals.discountDeal && (
            <React.Fragment>
              <div className="text-xs text-grey">reg. ${totals.price}</div>
              <div className="inline-block text-xs font-bold text-center text-white rounded bg-notification px-sm py-xs">
                {item.discountLabel}
              </div>
            </React.Fragment>
          )}
          {totals.dealExists && !totals.discountDeal && (
            <React.Fragment>
              <div className="text-xs text-grey">reg. ${totals.price}</div>
              <div className="text-xs font-bold text-notification py-xs">
                Save {`$${totals.savings}`}
              </div>
            </React.Fragment>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartItemCard;
